import dayjs from "dayjs";

export const STANDARD = "THRESHOLD_3_6";
export const CALENDAR_DAY = "CALENDAR_DAY";
export const TWENTY_FOUR_HOUR = "CONFIGURABLE_24_HOUR";

export const MODE_LABELS = {
  [STANDARD]: "Standard (3/6-hour)",
  [CALENDAR_DAY]: "Calendar day",
  [TWENTY_FOUR_HOUR]: "24-hour",
};

export const MODE_HELP = {
  [STANDARD]:
    "Full 24-hour blocks from admission. Leftover under 3h is free, 3h to under 6h is half a day, 6h or more is a full day. Not configurable.",
  [CALENDAR_DAY]:
    "Days change at midnight in the facility's timezone. Middle dates are full days; the first, last and same-day dates use the rules below.",
  [TWENTY_FOUR_HOUR]:
    "Full 24-hour blocks from admission. The leftover hours use the rules below.",
};

export const MODE_TABLES = {
  [STANDARD]: [],
  [CALENDAR_DAY]: [
    { key: "first_day", label: "First day", help: "Hours from admission to midnight" },
    { key: "last_day", label: "Last day", help: "Hours from midnight to discharge" },
    { key: "same_day", label: "Same-day stay", help: "Admitted and discharged on the same date" },
  ],
  [TWENTY_FOUR_HOUR]: [
    { key: "remainder", label: "Leftover hours", help: "Hours left after the full 24-hour blocks" },
  ],
};

export const FRACTIONS = [
  { value: 0, label: "No charge" },
  { value: 0.5, label: "Half day" },
  { value: 1, label: "Full day" },
];

const fractionLabel = (value) =>
  FRACTIONS.find((f) => f.value === Number(value))?.label || "-";

export const DEFAULT_TIERS = [
  { up_to_hours: 2, inclusive: true, fraction: 0 },
  { up_to_hours: 6, inclusive: false, fraction: 0.5 },
  { up_to_hours: null, fraction: 1 },
];

export const defaultConfiguration = (mode) =>
  Object.fromEntries(
    (MODE_TABLES[mode] || []).map(({ key }) => [key, DEFAULT_TIERS.map((t) => ({ ...t }))])
  );

// "Up to 2h: No charge · Over 2h, under 6h: Half day · From 6h: Full day"
export const describeTiers = (tiers = []) =>
  tiers
    .map((tier, index) => {
      const prev = tiers[index - 1];
      const lower = prev ? `${prev.inclusive ? "Over" : "From"} ${prev.up_to_hours}h` : "";
      if (tier.up_to_hours == null) {
        return `${lower || "Any stay"}: ${fractionLabel(tier.fraction)}`;
      }
      const upper = `${tier.inclusive ? "up to" : "under"} ${tier.up_to_hours}h`;
      const range = lower ? `${lower}, ${upper}` : upper.charAt(0).toUpperCase() + upper.slice(1);
      return `${range}: ${fractionLabel(tier.fraction)}`;
    })
    .join(" · ");

// Mirrors the backend rules so the admin sees problems before saving.
export const tierErrors = (tiers = []) => {
  const errors = {};
  let previousHours = 0;
  let previousFraction = 0;
  tiers.forEach((tier, index) => {
    const isLast = index === tiers.length - 1;
    const hours = tier.up_to_hours;
    if (!isLast) {
      if (hours == null || hours <= 0 || hours >= 24) {
        errors[index] = "Hours must be between 0 and 24";
      } else if (index && hours <= previousHours) {
        errors[index] = "Hours must increase from one rule to the next";
      }
      previousHours = hours ?? previousHours;
    }
    if (Number(tier.fraction) < previousFraction) {
      errors[index] = errors[index] || "Longer stays can't cost less";
    }
    previousFraction = Math.max(previousFraction, Number(tier.fraction));
  });
  return errors;
};

export const configurationErrors = (mode, configuration) =>
  (MODE_TABLES[mode] || []).some(({ key }) => Object.keys(tierErrors(configuration?.[key])).length);

const PATH_LABELS = {
  configuration: null,
  billing_mode: "Billing mode",
  effective_from: "Effective from",
  up_to_hours: "hours",
  fraction: "charge",
  stays: "Test stays",
};

// ["configuration", "last_day", 1, "up_to_hours"] -> "Last day, rule 2, hours"
export const pathLabel = (path = []) => {
  const tables = Object.fromEntries(
    Object.values(MODE_TABLES).flat().map(({ key, label }) => [key, label])
  );
  return path
    .map((part) => {
      if (typeof part === "number") return `row ${part + 1}`;
      if (tables[part]) return tables[part];
      return part in PATH_LABELS ? PATH_LABELS[part] : part;
    })
    .filter(Boolean)
    .join(", ");
};

export const apiError = (error, fallback) => {
  const data = error?.response?.data;
  const where = pathLabel(data?.err?.path);
  const text = data?.message || data?.detail || fallback;
  return where ? `${where}: ${text}` : text;
};

export const formatDateTime = (iso) => {
  if (!iso) return "-";
  if (dayjs(iso).year() <= 1970) return "Since the start";
  return dayjs(iso).format("DD MMM YYYY, hh:mm A");
};

// Backend times carry the facility's offset; show them as facility-local wall time.
export const facilityTime = (iso) =>
  iso ? dayjs(iso.slice(0, 19)).format("DD MMM, hh:mm A") : "-";

export const RULE_LABELS = {
  first_day: "First day",
  last_day: "Last day",
  same_day: "Same day",
  full_day: "Full day",
  full_period: "Full 24h",
  remainder: "Leftover",
  legacy_full: "Full 24h",
  legacy_remainder: "Leftover",
};

export const formatAmount = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
